/**
 * CodeCell — shared renderer for a line item's code column across PO and Bill
 * tables (v1.17).
 *
 * Two states:
 *   1. Resolved   — an internal inventory code exists. Shows the internal code
 *                   (orange, the system-of-record code) and, when present, the
 *                   supplier's own code beneath it (muted mono) so both are
 *                   always visible.
 *   2. Unresolved — no internal code yet. Shows a white-on-red "Enter / Select
 *                   Code" pill as a loud affordance, with the supplier code
 *                   still shown beneath. Clicking the pill calls onResolve so
 *                   the caller can open the ResolveCodeDialog.
 *
 * The supplier code is ALWAYS kept distinct from the internal code — this
 * component never conflates them.
 */

import React from 'react';
import { Box, Typography, Tooltip, Chip } from '@mui/material';
import EditNoteIcon from '@mui/icons-material/EditNote';
import { FI_ORANGE } from '../theme/tokens';

const RED_BG = '#c62828';
const RED_BG_HOVER = '#b71c1c';

export interface CodeCellProps {
  /** Internal inventory code. Empty/undefined means unresolved. */
  internalCode?: string;
  /** Supplier's own code for this line. Shown in both states when present. */
  supplierCode?: string;
  /** Called when the user clicks the unresolved pill (open resolve dialog). */
  onResolve?: () => void;
  /** Compact mode for dense reconciliation tables. */
  dense?: boolean;
  /** When true, the resolved internal code is itself clickable (re-resolve). */
  allowReresolve?: boolean;
  /**
   * When true, the supplier code is NOT shown stacked beneath the internal code
   * — use this where the table has a dedicated Supplier Code column already.
   */
  hideSupplier?: boolean;
  /** Service (labour) line — no internal inventory code required. */
  lineType?: 'product' | 'service';
}

export const CodeCell: React.FC<CodeCellProps> = ({
  internalCode,
  supplierCode,
  onResolve,
  dense = false,
  allowReresolve = false,
  hideSupplier = false,
  lineType = 'product',
}) => {
  const resolved = !!(internalCode && internalCode.trim());
  const showSupplier = !hideSupplier;
  const codeFs = dense ? '0.66rem' : '0.75rem';
  const supFs = dense ? '0.58rem' : '0.64rem';

  if (lineType === 'service') {
    return (
      <StackRow dense={dense}>
        <Typography sx={{ fontSize: codeFs, color: '#bbb' }}>—</Typography>
        <Chip label="Service" size="small" onClick={onResolve}
          sx={{ height: dense ? 16 : 18, fontSize: dense ? '0.54rem' : '0.58rem', cursor: onResolve ? 'pointer' : 'default',
            backgroundColor: '#eceff1', color: '#546e7a' }} />
        {showSupplier && supplierCode?.trim() ? (
          <Typography sx={{ fontSize: supFs, color: '#999', fontFamily: 'monospace' }}>Supplier: {supplierCode}</Typography>
        ) : null}
      </StackRow>
    );
  }

  if (!resolved) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.3, alignItems: 'flex-start' }}>
        <Tooltip title="No inventory code — click to match an existing item or create a new one" arrow>
          <Box
            onClick={onResolve}
            sx={{
              display: 'inline-flex', alignItems: 'center', gap: 0.4,
              backgroundColor: RED_BG, color: '#fff',
              fontWeight: 800, fontSize: dense ? '0.58rem' : '0.64rem',
              letterSpacing: '0.3px', textTransform: 'uppercase',
              px: dense ? 0.7 : 0.9, py: dense ? 0.25 : 0.35, borderRadius: 1,
              cursor: onResolve ? 'pointer' : 'default', whiteSpace: 'nowrap',
              boxShadow: '0 1px 2px rgba(198,40,40,0.4)',
              transition: 'background-color 0.12s',
              '&:hover': onResolve ? { backgroundColor: RED_BG_HOVER } : undefined,
            }}
          >
            <EditNoteIcon sx={{ fontSize: dense ? 12 : 14 }} />
            Enter / Select Code
          </Box>
        </Tooltip>
        {showSupplier && supplierCode && supplierCode.trim() ? (
          <Typography sx={{ fontSize: supFs, color: '#999', fontFamily: 'monospace', whiteSpace: 'nowrap' }}>
            Supplier: {supplierCode}
          </Typography>
        ) : null}
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.15, alignItems: 'flex-start' }}>
      <Typography
        onClick={allowReresolve ? onResolve : undefined}
        sx={{
          fontSize: codeFs, color: FI_ORANGE, fontWeight: 700, fontFamily: 'monospace',
          whiteSpace: 'nowrap',
          cursor: allowReresolve && onResolve ? 'pointer' : 'default',
          '&:hover': allowReresolve && onResolve ? { textDecoration: 'underline dotted' } : undefined,
        }}
      >
        {internalCode}
      </Typography>
      {showSupplier && supplierCode && supplierCode.trim() && supplierCode.trim().toLowerCase() !== internalCode!.trim().toLowerCase() ? (
        <Typography sx={{ fontSize: supFs, color: '#999', fontFamily: 'monospace', whiteSpace: 'nowrap' }}>
          Supplier: {supplierCode}
        </Typography>
      ) : null}
    </Box>
  );
};

const StackRow: React.FC<{ dense?: boolean; children: React.ReactNode }> = ({ dense, children }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: dense ? 0.2 : 0.3, alignItems: 'flex-start' }}>{children}</Box>
);

export default CodeCell;
